import type { Filter, FilterField } from '@/components/filter/constants';
import { getQuery } from '@/components/filter/helpers';
import type { Pagination } from '@/hooks/usePagination';
import { BaseApiClient } from '@/services/baseApiClient';
import request, { type RequestOptions } from '@/utils/request';
import type { StrictOmit } from '@enmon/commons';
import type {
  CountableApiResponse,
  CreateExportDto,
  ExportEntity,
  ExportType,
  ExportValidationResponse,
  MetersExportResult,
  PreviewExportDto,
  SerializedExport,
  SerializedUser,
  ValidateExportDto,
} from '@enmon/commons/lib/enmon';
import type { UpdateExportDto } from '@enmon/commons/lib/enmon/exports/dtos/update.dto';
import type { Pt1MessageData } from '@enmon/commons/lib/enmon/exports/ote/pt1';
import type { ResreportMessageCode } from '@enmon/commons/lib/enmon/exports/ote/resreport';
import type { Pf1MessageData } from '@enmon/commons/lib/enmon/exports/ote/pf1';
import type { Pd1MessageData } from '@enmon/commons/lib/enmon/exports/ote/pd1';
import type { E1Report, Eru } from '@enmon/commons/lib/enmon/exports/eru/e1';
import type { ResdataMessageCode } from '@enmon/commons/lib/enmon/exports/ote/resdata';
import type { ResFuelMessageCode } from '@enmon/commons/lib/enmon/exports/ote/resfuel';
import type { CdsidisMessageCode, CdsidisMessageData } from '@enmon/commons/lib/enmon/exports/ote/cdsidis';
import type { SupplierChargesData } from '@enmon/commons/lib/enmon/exports/ldn/supplierCharges';

// Populated exports
export type PopulatedExport = StrictOmit<SerializedExport, 'user'> & {
  user?: Pick<SerializedUser, '_id' | 'name' | 'phone' | 'email'>;
};

type GetOTEExportResult = StrictOmit<PopulatedExport, 'data'> & {
  data?: {
    pt1?: Pt1MessageData;
    pd1?: Pd1MessageData;
    pf1?: Pf1MessageData;
    e1?: E1Report;
    cdsidis?: CdsidisMessageData;
    supplierCharges?: SupplierChargesData;
  };
};

// Exports filter
export type ExportsFilter = Pick<Filter, FilterField.Search | FilterField.Customer> &
  Pagination & { type?: ExportType };

// Argument and payload types
export type DownloadExportArgs = {
  exportId: SerializedExport['_id'];
  exportMessage?:
    | ResreportMessageCode.PT1
    | ResdataMessageCode.PD1
    | ResFuelMessageCode.PF1
    | CdsidisMessageCode.RCS
    | Eru.E1;
};
export type GetExportsArgs = ExportsFilter;
export type GetOneExportArg = SerializedExport['_id'];
export type GetOneExportStatusArg = SerializedExport['_id'];
export type CreateExportArg = CreateExportDto;
export type UpdateExportArgs = {
  exportId: SerializedExport['_id'];
  body: UpdateExportDto;
};
export type DeleteExportArg = SerializedExport['_id'];
export type ExecuteExportArg = SerializedExport['_id'];
export type PreviewExportArg = PreviewExportDto;
export type ValidateExportArg = ValidateExportDto;

// Response types
export type GetExportsResult = CountableApiResponse<PopulatedExport[]> | null;
export type GetExportResult = GetOTEExportResult | null;
export type GetOneExportStatusResult = { status: SerializedExport['status'] } | null;
export type CreateExportResult = SerializedExport | null;
export type UpdateExportResult = GetOTEExportResult | null;
export type ExecuteExportResult = undefined | null;
export type DeleteExportResult = undefined | null;
export type DownloadExportResult = { accessToken: string } | null;
export type PreviewExportResult = unknown[] | MetersExportResult | null;
export type ValidateExportResult<T extends ExportEntity> = ExportValidationResponse<T> | null;

export class ExportsApiClient extends BaseApiClient {
  constructor() {
    super('api/exports');
  }

  public download({ exportId }: DownloadExportArgs): Promise<DownloadExportResult> {
    return request(`auth/token`, {
      method: 'POST',
      signal: undefined,
      body: JSON.stringify({ scope: 'export', exportId }),
    });
  }

  public get(filter: GetExportsArgs, signal: RequestOptions['signal']): Promise<GetExportsResult> {
    return this.http.get('', {
      query: getQuery(filter),
      signal,
    });
  }

  public getOne(exportId: GetOneExportArg, signal: RequestOptions['signal']): Promise<GetExportResult> {
    return this.http.get(`${exportId}`, { signal });
  }

  public getExportStatus(
    exportId: GetOneExportStatusArg,
    signal?: RequestOptions['signal'],
  ): Promise<GetOneExportStatusResult> {
    return this.http.get(`${exportId}/status`, { signal });
  }

  public create(createExportDto: CreateExportDto): Promise<CreateExportResult> {
    return this.http.post('', createExportDto);
  }

  public preview(arg: PreviewExportArg, signal: RequestOptions['signal']): Promise<PreviewExportResult> {
    return this.http.post('preview', arg, { signal });
  }

  public validate<T extends ExportEntity>(
    arg: ValidateExportArg,
    signal: RequestOptions['signal'],
  ): Promise<ValidateExportResult<T>> {
    return this.http.post('validate', arg, { signal });
  }

  public update({ exportId, body }: UpdateExportArgs): Promise<UpdateExportResult> {
    return this.http.put(`${exportId}`, body);
  }

  public execute(exportId: ExecuteExportArg): Promise<ExecuteExportResult> {
    return this.http.put(`${exportId}/execute`, {});
  }

  public delete(exportId: DeleteExportArg): Promise<DeleteExportResult> {
    return this.http.delete(`${exportId}`);
  }
}
